import {
  Anchor,
  Divider,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  TypographyStylesProvider,
} from '@mantine/core'
import dayjs from 'dayjs'
import { CustomHTML } from '~/components/custom-html'
import MultiLineText from '~/components/multi-line-text'

export const SkeletonCompanyDetail = () => {
  return (
    <Stack>
      <Skeleton height={240} w='100%' mt='sm' />

      <Table variant='vertical' withTableBorder={false}>
        <Table.Tbody>
          {Array.from({ length: 8 }).map((_, index) => {
            const componentKey = `skeleton-company-detail-table-${index}`
            return (
              <Table.Tr key={componentKey}>
                <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                  <Skeleton height={20} width={100} />
                </Table.Th>
                <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                  <Skeleton height={20} width={100} />
                </Table.Th>

                <Table.Td>
                  <Group gap='xs'>
                    <Skeleton height={20} width='100%' />
                  </Group>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  )
}

type Props = {
  data: {
    industries: { id: string; name: string }[]
    businessContent: string
    foundedOn: Date
    capital: string
    ceo: string
    employees: string | null
    address: string
    mapUrl: string
    history: string | null
    description: string
  }
}

export const CompanyDetail = (props: Props) => {
  const { data } = props

  return (
    <>
      <Stack gap='xl'>
        <CustomHTML content={data.description ?? ''} />
      </Stack>

      <Title order={3} c='primary' my='xl'>
        企業データ
      </Title>

      <Table
        variant='vertical'
        styles={{ th: { backgroundColor: 'transparent' } }}
        withTableBorder={false}
      >
        <Table.Tbody>
          {data.industries.length !== 0 && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>業界</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>業界</Title>
              </Table.Th>

              <Table.Td>
                <Group gap='xs'>
                  {data.industries.flatMap((industry, index) => [
                    <Text key={`text-${industry.id}`}>{industry.name}</Text>,
                    index < data.industries.length - 1 && (
                      <Divider
                        orientation='vertical'
                        key={`vertical-${industry}`}
                      />
                    ),
                  ])}
                </Group>
              </Table.Td>
            </Table.Tr>
          )}

          {data.businessContent && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>事業内容</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>事業内容</Title>
              </Table.Th>
              <Table.Td>
                <MultiLineText>{data.businessContent}</MultiLineText>
              </Table.Td>
            </Table.Tr>
          )}

          {data.foundedOn && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>設立年月</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>設立年月</Title>
              </Table.Th>
              <Table.Td>
                <Text>{dayjs(data.foundedOn).format('YYYY年M月')}</Text>
              </Table.Td>
            </Table.Tr>
          )}

          {data.capital && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>資本金</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>資本金</Title>
              </Table.Th>
              <Table.Td>
                <Text>{data.capital}</Text>
              </Table.Td>
            </Table.Tr>
          )}

          {data.ceo && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>代表者名</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>代表者名</Title>
              </Table.Th>
              <Table.Td>
                <Text>{data.ceo}</Text>
              </Table.Td>
            </Table.Tr>
          )}

          {data.employees && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>従業員数</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>従業員数</Title>
              </Table.Th>
              <Table.Td>
                <MultiLineText>{data.employees}</MultiLineText>
              </Table.Td>
            </Table.Tr>
          )}

          {data.mapUrl && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>本社所在地</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>本社所在地</Title>
              </Table.Th>
              <Table.Td>
                <Anchor href={data.mapUrl} target='_blank' underline='never'>
                  {data.address}
                </Anchor>
              </Table.Td>
            </Table.Tr>
          )}

          {data.history && (
            <Table.Tr>
              <Table.Th w={80} py='xl' px={0} hiddenFrom='sm'>
                <Title order={4}>沿革</Title>
              </Table.Th>
              <Table.Th w={160} py='xl' px={0} visibleFrom='sm'>
                <Title order={4}>沿革</Title>
              </Table.Th>
              <Table.Td>
                <MultiLineText>{data.history}</MultiLineText>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </>
  )
}
